import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/contexts/LocaleContext';
import { LOCALE_CONFIG, LocaleKey } from '@/i18n';

interface HreflangLink {
  hreflang: string;
  href: string;
}

interface SeoMetaConfig {
  path: string;
  includeXDefault?: boolean;
}

/**
 * Maps locale keys to hreflang codes
 */
const HREFLANG_MAP: Record<LocaleKey, string> = {
  es: 'es',
  en: 'en',
  'en-EU': 'en-EU',
  'pt-BR': 'pt-BR',
};

/**
 * Maps locale keys to full domain URLs for .com domain
 * www.foradarota.com is the primary Spanish domain
 */
const DOMAIN_MAP_COM: Record<LocaleKey, string> = {
  es: 'https://www.foradarota.com',
  en: 'https://en.foradarota.com',
  'en-EU': 'https://eu.foradarota.com',
  'pt-BR': 'https://br.foradarota.com',
};

/**
 * Maps locale keys to full domain URLs for .com.br domain
 * www.foradarota.com.br is the primary Portuguese domain
 */
const DOMAIN_MAP_COM_BR: Record<LocaleKey, string> = {
  es: 'https://es.foradarota.com.br',
  en: 'https://en.foradarota.com.br',
  'en-EU': 'https://eu.foradarota.com.br',
  'pt-BR': 'https://www.foradarota.com.br',
};

/**
 * Get the current domain family (.com or .com.br)
 */
const getCurrentDomainFamily = (): 'com' | 'com.br' => {
  if (typeof window === 'undefined') return 'com';
  return window.location.hostname.endsWith('.com.br') ? 'com.br' : 'com';
};

/**
 * Get domain map based on current site
 */
const getDomainMap = (): Record<LocaleKey, string> => {
  return getCurrentDomainFamily() === 'com.br' ? DOMAIN_MAP_COM_BR : DOMAIN_MAP_COM;
};

/**
 * Maps locale keys to og:locale format
 */
const OG_LOCALE_MAP: Record<LocaleKey, string> = {
  es: 'es_ES',
  en: 'en_US',
  'en-EU': 'en_GB',
  'pt-BR': 'pt_BR',
};

/**
 * Normalize path to ensure consistent URL format
 * - Root path "/" stays as empty string (no trailing slash on domain)
 * - Other paths start with "/" but don't end with "/"
 */
const normalizePath = (path: string): string => {
  // Handle root path - return empty string so we get just the domain
  if (path === '/' || path === '') {
    return '';
  }
  // Ensure path starts with / and doesn't end with /
  let cleanPath = path.startsWith('/') ? path : `/${path}`;
  cleanPath = cleanPath.endsWith('/') ? cleanPath.slice(0, -1) : cleanPath;
  return cleanPath;
};

/**
 * Get the full URL for a given locale and path
 * Uses the current domain family (.com or .com.br)
 */
export const getLocalizedUrl = (locale: LocaleKey, path: string): string => {
  const domainMap = getDomainMap();
  const domain = domainMap[locale];
  const normalizedPath = normalizePath(path);
  return `${domain}${normalizedPath}`;
};

/**
 * Get the canonical URL for the current locale
 */
export const getCanonicalUrl = (locale: LocaleKey, path: string): string => {
  return getLocalizedUrl(locale, path);
};

/**
 * Generate hreflang links for all supported locales
 */
export const getHreflangLinks = (path: string, currentLocale: LocaleKey, includeXDefault = true): HreflangLink[] => {
  const locales = Object.keys(LOCALE_CONFIG) as LocaleKey[];
  
  const links: HreflangLink[] = locales.map(locale => ({
    hreflang: HREFLANG_MAP[locale],
    href: getLocalizedUrl(locale, path),
  }));
  
  // Add x-default - points to English as the fallback for users without a matching language
  // Using 'en' instead of 'es' to avoid canonical conflict with Spanish version
  if (includeXDefault) {
    links.push({
      hreflang: 'x-default',
      href: getLocalizedUrl('en', path),
    });
  }
  
  return links;
};

/**
 * Get the HTML lang attribute value for the current locale
 */
export const getHtmlLang = (locale: LocaleKey): string => {
  switch (locale) {
    case 'pt-BR':
      return 'pt-BR';
    case 'en-EU':
      return 'en-GB';
    case 'en':
      return 'en-US';
    case 'es':
    default:
      return 'es';
  }
};

/**
 * Hook that provides SEO meta helpers for the current locale
 */
export const useSeoMeta = (config: SeoMetaConfig) => {
  const { locale } = useLocale();
  const { i18n } = useTranslation();
  
  return useMemo(() => {
    const currentLocale = locale as LocaleKey;
    
    return {
      canonicalUrl: getCanonicalUrl(currentLocale, config.path),
      hreflangLinks: getHreflangLinks(config.path, currentLocale, config.includeXDefault ?? true),
      ogLocale: OG_LOCALE_MAP[currentLocale],
      htmlLang: getHtmlLang(currentLocale),
      alternateLocales: Object.keys(LOCALE_CONFIG)
        .filter(l => l !== currentLocale)
        .map(l => OG_LOCALE_MAP[l as LocaleKey]),
    };
  }, [locale, config.path, config.includeXDefault]);
};

export default useSeoMeta;
